/** RaiderMapCR - Campus events page */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Button,
  CardActions
} from '@mui/material';
import { Event as EventIcon, CalendarToday as CalendarIcon, LocationOn as LocationIcon } from '@mui/icons-material';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/admin/events')
      .then(response => response.json())
      .then(data => {
        setEvents(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching events:', error);
        setLoading(false);
      });
  }, []);

  const addToCalendar = (event) => {
    console.log('Adding event to calendar:', event);
  };

  if (loading) {
    return (
      <div className="main-content">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#ffffff' }}>
            Campus Events
          </Typography>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Stay updated with events and activities
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {events.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    Here you will be able to see upcoming events and add them to your calendar.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            events.map((event, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#ffffff' }}>
                      {event.title}
                    </Typography>
                    
                    <Box sx={{ my: 2 }}>
                      <Chip 
                        icon={<CalendarIcon sx={{ color: '#ffffff' }} />}
                        label={event.date}
                        size="small" 
                        sx={{ 
                          mr: 1, 
                          mb: 1,
                          backgroundColor: 'rgba(42, 42, 42, 0.85)',
                          color: '#ffffff',
                          border: '1px solid rgba(74, 74, 74, 0.6)'
                        }}
                      />
                      <Chip 
                        label={event.time}
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          mr: 1, 
                          mb: 1,
                          backgroundColor: 'rgba(42, 42, 42, 0.85)',
                          color: '#ffffff',
                          border: '1px solid rgba(74, 74, 74, 0.6)'
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: '#ffffff', paragraph: true, mb: 3 }}>
                      {event.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <LocationIcon sx={{ mr: 1, fontSize: 16, color: '#ffffff' }} />
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        {event.location}
                      </Typography>
                    </Box>

                    {event.organizer && (
                      <Typography variant="body2" sx={{ color: '#ffffff', mb: -1.5 }}>
                        Organized by: {event.organizer}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center' }}>
                    <Button 
                      variant="contained"
                      size="small" 
                      onClick={() => addToCalendar(event)}
                      startIcon={<CalendarIcon />}
                      sx={{
                        color: '#ffffff',
                        backgroundColor: 'primary.main',
                        border: '1px solid primary.main',
                        minWidth: '170px',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          borderColor: 'primary.dark',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        }
                      }}
                    >
                      Add to My Calendar
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default EventsPage;
